// src/hooks/useChat.ts
// Custom hook untuk mengelola state dan logic chatbot

import { useState, useCallback, useRef } from "react";
import { ChatMessage, NoiseSummary, FlyToTarget } from "../types/chatTypes";
import { NoiseCluster } from "../types/mapTypes";
import { sendToGemini } from "../services/geminiService";
import { buildRAGContext } from "../services/ragService";
import {
  computeNoiseSummaryFromClusters,
  detectLocationQuery,
  findClusterForCategory,
} from "../services/noiseDataService";

function generateId(): string {
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const WELCOME_MESSAGE: ChatMessage = {
  id: "welcome",
  role: "assistant",
  content:
    "Halo! 👋 Saya **EcoNoise Assistant**.\n\n" +
    "Saya bisa membantu Anda tentang:\n" +
    "- 📊 Statistik wilayah kebisingan\n" +
    "- 📍 Menunjukkan lokasi wilayah tertentu di peta\n" +
    "- 🏥 Dampak kebisingan & tips\n\n" +
    'Coba tanya: *"Tunjukkan wilayah tenang di mana?"*',
  timestamp: new Date(),
};

interface UseChatOptions {
  onFlyTo?: (target: FlyToTarget) => void;
}

export function useChat(clusters: NoiseCluster[], options?: UseChatOptions) {
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const getNoiseSummary = useCallback((): NoiseSummary => {
    return computeNoiseSummaryFromClusters(clusters);
  }, [clusters]);

  /**
   * Mengirim pesan user dan mendapatkan response dari Gemini + RAG.
   * Jika user bertanya lokasi wilayah tertentu, otomatis flyTo peta.
   */
  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) return;

      const userMsg: ChatMessage = {
        id: generateId(),
        role: "user",
        content: trimmed,
        timestamp: new Date(),
      };

      const loadingMsg: ChatMessage = {
        id: generateId(),
        role: "assistant",
        content: "",
        timestamp: new Date(),
        isLoading: true,
      };

      setMessages((prev) => [...prev, userMsg, loadingMsg]);
      setIsLoading(true);

      try {
        // 1. Hitung noise summary
        const noiseSummary = getNoiseSummary();

        // 2. Deteksi apakah user minta navigasi ke lokasi tertentu
        const locationCategory = detectLocationQuery(trimmed);
        let flyTarget: FlyToTarget | null = null;

        if (locationCategory) {
          flyTarget = findClusterForCategory(noiseSummary, locationCategory);
          if (flyTarget && options?.onFlyTo) {
            // Trigger flyTo ke lokasi yang diminta
            options.onFlyTo(flyTarget);
          }
        }

        // 3. Bangun konteks RAG (compact)
        const ragContext = buildRAGContext(trimmed, noiseSummary);

        // 4. History — hanya 6 pesan terakhir untuk hemat token
        const recentMessages = messages
          .filter((m) => !m.isLoading && m.id !== "welcome")
          .slice(-6);
        const history = recentMessages.map((m) => ({
          role: m.role === "assistant" ? "assistant" : "user",
          // Trim content panjang di history untuk hemat token
          content: m.content.length > 300 ? m.content.slice(0, 300) + "..." : m.content,
        }));

        // 5. Kirim ke Gemini
        const response = await sendToGemini({
          prompt: trimmed,
          context: ragContext || undefined,
          history,
        });

        // 6. Jika ada flyTo, tambahkan info navigasi ke response
        let responseText = response.text;
        if (flyTarget && response.success) {
          responseText += `\n\n📍 *Peta telah diarahkan ke wilayah ${flyTarget.category} (${flyTarget.noiseLevel.toFixed(1)} dB)*`;
        } else if (locationCategory && !flyTarget) {
          responseText += `\n\n⚠️ Tidak ditemukan wilayah ${locationCategory} di peta saat ini.`;
        }

        const assistantMsg: ChatMessage = {
          id: loadingMsg.id,
          role: "assistant",
          content: responseText,
          timestamp: new Date(),
          metadata: {
            noiseSummary: noiseSummary.total > 0 ? noiseSummary : undefined,
          },
        };

        setMessages((prev) =>
          prev.map((m) => (m.id === loadingMsg.id ? assistantMsg : m))
        );
      } catch (error) {
        console.error("Chat error:", error);
        const errorMsg: ChatMessage = {
          id: loadingMsg.id,
          role: "assistant",
          content: "Maaf, terjadi kesalahan. Silakan coba lagi. 🙏",
          timestamp: new Date(),
        };
        setMessages((prev) =>
          prev.map((m) => (m.id === loadingMsg.id ? errorMsg : m))
        );
      } finally {
        setIsLoading(false);
      }
    },
    [messages, isLoading, getNoiseSummary, options]
  );

  const toggleChat = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const clearChat = useCallback(() => {
    setMessages([WELCOME_MESSAGE]);
  }, []);

  return {
    messages,
    isLoading,
    isOpen,
    sendMessage,
    toggleChat,
    clearChat,
    noiseSummary: getNoiseSummary(),
  };
}
